import React from "react";
import { useTranslation } from "react-i18next";
import { Check, X, HelpCircle } from "lucide-react";
import { SwipeOption, VoteType } from "./utils/types";
import { formatDate, formatTime } from "./utils/dateUtils";

export interface SlotVoteRowProps {
  option: SwipeOption;
  userVote: VoteType | null;
  showDayBanner: boolean;
  onVote: (optionId: string, voteType: VoteType | null) => void;
  allowMaybeVotes?: boolean;
}

type Choice = {
  type: VoteType;
  Icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  activeClass: string;
};

const CHOICES_FULL: Choice[] = [
  {
    type: "yes",
    Icon: Check,
    labelKey: "slot_vote_row.yes",
    activeClass: "bg-success/20 border-success text-success",
  },
  {
    type: "maybe",
    Icon: HelpCircle,
    labelKey: "slot_vote_row.maybe",
    activeClass: "bg-warning/20 border-warning text-warning",
  },
  {
    type: "no",
    Icon: X,
    labelKey: "slot_vote_row.no",
    activeClass: "bg-destructive/20 border-destructive text-destructive",
  },
];

const SlotVoteRow: React.FC<SlotVoteRowProps> = ({
  option,
  userVote,
  showDayBanner,
  onVote,
  allowMaybeVotes = true,
}) => {
  const { t } = useTranslation("common");
  const dayLabel = option.date_group_label || formatDate(option.option_date);
  const timeLabel =
    !option.date_group_label && option.time_slots
      ? formatTime(option.time_slots)
      : null;
  const choices = allowMaybeVotes
    ? CHOICES_FULL
    : CHOICES_FULL.filter((c) => c.type !== "maybe");

  const handleClick = (type: VoteType) => {
    onVote(option.id, userVote === type ? null : type);
  };

  return (
    <div data-testid={`slot-vote-row-${option.id}`}>
      {showDayBanner && (
        <div
          className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          data-testid={`slot-day-banner-${option.id}`}
        >
          {dayLabel}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 px-3 h-11 border-b border-border/60">
        <div className="min-w-0 flex-1 text-sm">
          {timeLabel ? (
            <span className="text-foreground">{timeLabel}</span>
          ) : (
            <span className="text-foreground">{dayLabel}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {choices.map(({ type, Icon, labelKey, activeClass }) => {
            const active = userVote === type;
            const label = t(labelKey);
            return (
              <button
                key={type}
                type="button"
                onClick={() => handleClick(type)}
                data-testid={`slot-vote-${type}-${option.id}`}
                data-state={active ? "active" : "inactive"}
                aria-pressed={active}
                aria-label={t("slot_vote_row.vote_aria", {
                  label,
                  slot: `${dayLabel}${timeLabel ? ` ${timeLabel}` : ""}`,
                })}
                className={`w-9 h-9 rounded-md border-2 flex items-center justify-center transition-colors ${
                  active
                    ? activeClass
                    : "bg-muted/40 border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SlotVoteRow;
